import React, { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { GradientLine, StripeLine, SEGPCT } from './gradientLines'
import { Popover } from 'antd-mobile'
import { getSum, Pluralize, filterHealthySignalsByDate } from '../../../../utils/common'
import { AiOutlineLeft, AiOutlineRight, AiOutlineUp, AiOutlineDown, AiOutlinePause } from 'react-icons/ai'
import { sleepModifierMappings } from '../../../../utils'

const ArrowIndicator = styled.span`
  svg {
    width: 14px;
    height: 14px;
  }
`

const IndicatorWrapper = styled.span`
  display: flex;
  align-items: center;
`

const UpArrow = () => <AiOutlineUp style={{ color: '#1976d2', fontSize: '14px' }} />

const DownArrow = () => <AiOutlineDown style={{ color: '#d32f2f', fontSize: '14px' }} />

const PauseIndicator = () => <AiOutlinePause style={{ color: '#1db954', fontSize: '14px', transform: 'rotate(90deg)' }} />

const SegmentLineContainer = styled.div`
  position: relative;
  z-index: 2;
  height: 2.2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  span {
    font-size: 0.75rem;
    color: #5b6177;
    font-family: Inter, Gilroy;
    &.no-target {
      margin-top: 0.9375rem;
    }
  }
  .bar-container {
    position: relative;
    width: calc(100vw - 7.5rem);
    .stripe-line svg {
      border-radius: 2rem;
    }
    .gradient-line {
      position: absolute;
      top: 0;
      overflow: hidden;
    }
    .workout-line-score {
      position: absolute;
      top: 6px;
      font-weight: 700;
      @media screen and (min-width: 481px) {
        top: -0.375rem;
      }
    }
  }
`

function createSleepModifierObjects(healthySignalsData, sleepData, overviewSummary, domainLabel) {
  return (Array.isArray(healthySignalsData) ? healthySignalsData : [])
    .filter(item => filterHealthySignalsByDate([item], domainLabel, sleepModifierMappings).length > 0)
    .map((item) => {
      const hsd_pct = Number(item[`${sleepModifierMappings[domainLabel]}`]).toFixed(0)
      
      let detailedSleepData = null
      if (overviewSummary && Array.isArray(overviewSummary.entries)) {
        detailedSleepData = overviewSummary.entries.filter(
          entry => entry.pillar === 'rejuvenation' && entry.type === 'sleep' && entry.record_date === item.record_date
        )
      }
      
      return {
        id: `sleep-${item.record_date}`,
        activity_type_shown: 'Sleep',
        category: domainLabel,
        duration: detailedSleepData && detailedSleepData.length > 0 ? detailedSleepData.reduce((sum, entry) => sum + (entry.details?.asleep_time || 0), 0) : 0,
        hsd_pct: Number(hsd_pct),
        hrv: detailedSleepData && detailedSleepData.length > 0 ? detailedSleepData.reduce((sum, entry) => sum + (entry.details?.heart_rate_variation || 0), 0) : 0,
        intensity: detailedSleepData && detailedSleepData.length > 0 ? detailedSleepData.reduce((sum, entry) => sum + (entry.details?.sleep_efficiency || 0), 0) : 0,
        record_date: item.record_date
      }
    })
}

function getBarPCTWidth(pct = 0, ranges = []) {
  const rangeCount = ranges.length - 1
  const targetRangeIndex = ranges.findIndex(
    (value, index) =>
      value <= pct && (ranges[index + 1] > pct || !ranges[index + 1])
  )

  let widthPCT = targetRangeIndex * (100 / rangeCount)
  if (pct > ranges[targetRangeIndex] && ranges[targetRangeIndex + 1]) {
    const extra = pct - ranges[targetRangeIndex]
    widthPCT +=
      ((extra / (ranges[targetRangeIndex + 1] - ranges[targetRangeIndex])) *
        100) /
      rangeCount
  }

  return widthPCT
}

function formatWorkoutDateTime(workout) {
  if (!workout || typeof workout !== 'object') {
    return null
  }

  try {
    if (workout.start_time && (typeof workout.start_time === 'number' || typeof workout.start_time === 'string')) {
      const timestamp = Number(workout.start_time)
      if (!isNaN(timestamp) && timestamp > 0) {
        const dateTime = moment(timestamp * 1000).utc().subtract(8, 'hours')
        if (dateTime.isValid()) {
          return dateTime.format('h:mm A - MMM D, YYYY')
        }
      }
    }

    if (workout.record_date && (typeof workout.record_date === 'string' || workout.record_date instanceof Date)) {
      const recordDate = moment(workout.record_date)
      if (recordDate.isValid()) {
        return recordDate.format('MMM D, YYYY')
      }
    }

    return null
  } catch (error) {
    console.warn('Error formatting workout date/time:', error, workout)
    return null
  }
}

export default function SegmentLine(props) {
  const {
    seg_label,
    score,
    workouts,
    svgWidth,
    target,
    ranges,
    widthReference,
    data,
    hightestValue,
    id
  } = props

  const width = getBarPCTWidth(data[widthReference], ranges) + '%'
  const targetWidth = target && getBarPCTWidth(target, ranges) + '%'
  return (
    <SegmentLineContainer>
      <span className={target ? '' : 'no-target'}>{seg_label}</span>
      <div className="bar-container">
        {target && (
          <div>
            <StripeLine
              svgWidth={svgWidth}
              target={target}
              hightestValue={hightestValue}
              id={`segment_${id}`}
            />
          </div>
        )}
        <div className="gradient-line" style={{ width }}>
          <GradientLine
            svgWidth={svgWidth}
            hightestValue={hightestValue}
            id={id}
          />
        </div>
        <div className="workout-line-score" style={{ top: -8 }}>
          <SEGPCT
            {...{
              width,
              text: score,
              svgWidth,
              target,
              targetWidth,
              hightestValue,
              id
            }}
          />
        </div>
        {workouts && (
          <Workouts
            totalPCT={score}
            workouts={workouts}
            ranges={ranges}
            totalWidth={width}
            label={seg_label}
          />
        )}
      </div>
    </SegmentLineContainer>
  )
}

const PopContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #5b6177;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.5rem 0;
  & > svg {
    color: #a4b9d2;
    &:first-child {
      margin-right: 0.5rem;
    }
    &:last-child {
      margin-left: 0.5rem;
    }
  }
  .popup-content {
    display: flex;
    padding: 0 1rem;
  }
  .score {
    width: 2.875rem;
    height: 2.875rem;
    line-height: 2.875rem;
    text-align: center;
    margin-left: 0.625rem;
    border-radius: 100%;
    background: #f6f6f7;
    color: #3d4358;
    font-size: 1rem;
    font-weight: 700;
    font-family: 'Inter';
  }
  .name {
    margin-bottom: 0.625rem;
    span {
      color: #94a3af;
      font-size: 0.5625rem;
    }
  }
  .info {
    display: flex;
    gap: 1.5rem;
    .workout-data-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.5rem;
    }
    span {
      color: #94a3af;
      font-size: 0.5625rem;
    }
  }
`

const WorkoutContainer = styled.div`
  width: 100%;
  position: absolute;
  top: 4px;
  display: flex;
  z-index: 9;
  &.target-cursor {
    min-height: 0.625rem;
  }

  .workout-item {
    border: 1px solid #ffffff;
    height: 8px;
    border-radius: 8px;
    outline: 1px solid #ffffff;
    box-sizing: border-box;
    margin-top: 1px;
    &:last-child {
      border-right: none;
    }
    &:hover,
    &.active {
      border-color: #0272e1;
      border-right: 1px solid;
      z-index: 2;
    }
  }
`

function getSleepDurationIndicator(duration, avgDuration) {
  if (!avgDuration || !duration) return <PauseIndicator />
  const diff = Math.abs(duration - avgDuration)
  const threshold = 0.5 * 60 * 60
  if (diff <= threshold) return <PauseIndicator />
  if (duration < avgDuration - threshold) return <DownArrow />
  if (duration > avgDuration + threshold) return <UpArrow />
  return <PauseIndicator />
}

function getSleepEfficiencyIndicator(efficiency, avgEfficiency) {
  if (!avgEfficiency || !efficiency) return <PauseIndicator />
  const val = Number(efficiency) || 0
  const avg = Number(avgEfficiency) || 0
  const threshold = 0.5
  if (Math.abs(val - avg) <= threshold) return <PauseIndicator />
  if (val < avg - threshold) return <DownArrow />
  if (val > avg + threshold) return <UpArrow />
  return <PauseIndicator />
}

function getSleepHrvIndicator(hrv, avgHrv) {
  if (!avgHrv || !hrv) return <PauseIndicator />
  const val = Number(hrv) || 0
  const avg = Number(avgHrv) || 0
  const threshold = 0.5
  if (Math.abs(val - avg) <= threshold) return <PauseIndicator />
  if (val < avg - threshold) return <DownArrow />
  if (val > avg + threshold) return <UpArrow />
  return <PauseIndicator />
}

function Workouts(props) {
  const {
    ranges,
    totalWidth,
    containerStyle,
    targetCursor,
    target,
    totalPCT,
    setTotalGradientWidth,
    label,
    showWorkoutPopup,
    sleepData,
    healthySignals,
    overviewSummary
  } = props

  const [workouts, setWorkouts] = useState([])
  const [visibleIndex, setVisibleIndex] = useState(null)
  
  const sleepRollingAvg30Days = overviewSummary?.sleepRollingAvg30Days || {}
  const avgDuration = sleepRollingAvg30Days?.asleep_time || 0
  const avgEfficiency = sleepRollingAvg30Days?.sleep_efficiency || 0
  const avgHrv = sleepRollingAvg30Days?.sleep_hrv || 0
  
  useEffect(() => {
    // Step 1: Create sleep modifier objects
    const sleep_modifier = createSleepModifierObjects(healthySignals, sleepData, overviewSummary, label)
    
    // Step 2: Combine workouts with sleep modifiers
    const target_workouts = [...(props.workouts || []), ...sleep_modifier].sort((a, b) => a.record_date.localeCompare(b.record_date))
    
    // Step 3: Calculate positions and widths
    let ratio = 1
    let totalPct = 0,
      position = 0
    for (const workout of target_workouts) {
      const { hsd_pct } = workout
      totalPct += hsd_pct
      
      const width = getBarPCTWidth(totalPct * ratio, ranges)
      workout.left = position + '%'
      const calculatedWidth = Math.floor(width - position)
      // Ensure minimum width for visibility
      workout.width = Math.max(calculatedWidth, 1) + '%'
      position = Math.floor(width)
    }

    if (totalWidth) {
      const _totalWidth = Math.floor(Number(totalWidth.replace('%', '')))
      const __totalWidth = getSum(
        target_workouts.map((item) => Number(item.width.replace('%', '')))
      )
      if (_totalWidth < __totalWidth) {
        const diff = (__totalWidth - _totalWidth) / target_workouts.length
        target_workouts.forEach((item) => {
          item.width = Number(item.width.replace('%', '')) - diff + '%'
        })
      }
    }

    const recalculateTotalWidth = getSum(
      target_workouts.map((item) => Number(item.width.replace('%', '')))
    )
    setTotalGradientWidth(recalculateTotalWidth + '%')

    setWorkouts(target_workouts)
  }, [props.workouts, ranges, totalWidth, setTotalGradientWidth, healthySignals, sleepData, label, overviewSummary])

  useEffect(() => {
    if (label === 'Aerobic Fitness' && showWorkoutPopup) {
      setVisibleIndex(workouts.length - 1)
    }
  }, [workouts, showWorkoutPopup, label])

  let afterRight = '0'
  if (targetCursor) {
    afterRight = 100 - getBarPCTWidth(target, ranges) + '%'
  }

  return (
    <WorkoutContainer
      className={` ${targetCursor ? 'target-cursor' : ''}`}
      style={containerStyle || {}}
      afterRight={afterRight}
    >
      {targetCursor && (<div
        style={{
          position: 'absolute',
          right: afterRight,
          top: '-5px',
          zIndex: 10
        }}
      >
        <svg width="10" height="10" viewBox="0 0 10 10">
          <circle cx="5" cy="5" r="5" fill="rgba(48, 140, 233, 1)" />
          <circle cx="5" cy="5" r="3.4" fill="white" />
          <circle cx="5" cy="5" r="2" fill="rgba(48, 140, 233, 1)" />
        </svg>
      </div>
      )}
      {workouts.map((item, index) => {
        const isWorkout = item.activity_type_shown !== 'Sleep'
        const isLastWorkout = isWorkout && index === workouts.length - 1
        const checkmarkLeft = `calc(${item.left} + ${item.width})`
        const isOverlap = Number(target) === 100 && Number(totalPCT) === 100
        
        const PopoverContent = isWorkout ? (
          <PopContent>
            {index > 0 && (
              <AiOutlineLeft
                onClick={() => {
                  const newIndex = Math.max(0, visibleIndex - 1)
                  setVisibleIndex(newIndex)
                }}
              />
            )}
            <div className="popup-content">
              <div>
                <div className="name">
                  {item.activity_type_shown || item.type}
                  <div
                    style={{
                      fontSize: '0.5rem',
                      color: '#94a3af',
                      marginTop: '0.375rem'
                    }}
                  >
                    {formatWorkoutDateTime(item)}
                  </div>
                </div>

                <div className="info">
                  <div>
                    <span>Time</span>
                    <br />
                    {Math.round(item.duration / 60)}{' '}
                    {Pluralize('min', Math.round(item.duration / 60) || 1)}
                  </div>
                  <div>
                    <span>Intensity</span>
                    <br />
                    {item.intensity}
                  </div>
                </div>
              </div>
              <div className="score">{item.hsd_pct}</div>
            </div>
            {index < workouts.length - 1 && (
              <AiOutlineRight
                onClick={() => {
                  const newIndex = Math.min(
                    workouts.length - 1,
                    visibleIndex + 1,
                  )
                  setVisibleIndex(newIndex)
                }}
              />
            )}
          </PopContent>
        ) : (
          <PopContent>
            {index > 0 && (
              <AiOutlineLeft
                onClick={() => {
                  const newIndex = Math.max(0, visibleIndex - 1)
                  setVisibleIndex(newIndex)
                }}
              />
            )}
            <div className="popup-content">
              <div>
                <div className="name">
                  Sleep Booster
                  <div
                    style={{
                      fontSize: '0.5rem',
                      color: '#94a3af',
                      marginTop: '0.375rem'
                    }}
                  >
                    {moment(item.record_date).format('MMM D, YYYY')}
                  </div>
                </div>

                <div className="info">
                  <div className="workout-data-container">
                    <span>Duration</span>
                    <IndicatorWrapper>
                      <ArrowIndicator>
                        {getSleepDurationIndicator(item.duration, avgDuration)}
                      </ArrowIndicator>
                    </IndicatorWrapper>
                  </div>
                  <div className="workout-data-container">
                    <span>Efficiency</span>
                    <IndicatorWrapper>
                      <ArrowIndicator>
                        {getSleepEfficiencyIndicator(
                          item.intensity,
                          avgEfficiency,
                        )}
                      </ArrowIndicator>
                    </IndicatorWrapper>
                  </div>
                  <div className="workout-data-container">
                    <span>HRV</span>
                    <IndicatorWrapper>
                      <ArrowIndicator>
                        {getSleepHrvIndicator(item.hrv, avgHrv)}
                      </ArrowIndicator>
                    </IndicatorWrapper>
                  </div>
                  <div className="workout-data-container">
                    <span>Sleep Booster</span>
                    <div style={{ marginTop: '2px'}}>{item.hsd_pct}</div>
                  </div>
                </div>
              </div>
              {/* <div className="score">{item.hsd_pct}</div> */}
            </div>
            {index < workouts.length - 1 && (
              <AiOutlineRight
                onClick={() => {
                  const newIndex = Math.min(
                    workouts.length - 1,
                    visibleIndex + 1,
                  )
                  setVisibleIndex(newIndex)
                }}
              />
            )}
          </PopContent>
        )

        return (
          <Popover
            trigger="click"
            placement="top"
            visible={visibleIndex === index}
            onVisibleChange={(visible) => {
              if (visible) {
                setVisibleIndex(index)
              } else if (index === visibleIndex) {
                setVisibleIndex(null)
              }
            }}
            content={PopoverContent}
            key={item.id || `workout-${index}`}
          >
            <div
              className={`workout-item ${visibleIndex === index ? 'active' : ''}`}
              style={{
                width: item.width < '1%' ? '1%' : item.width,
                left: item.left,
                background: isWorkout ? undefined : 'rgb(92, 0, 209)',
                height: '8px',
                marginTop: '1px'
              }}
            >
            </div>
          </Popover>
        )
      })}
    </WorkoutContainer>
  )
}

export function CompactLine(props) {
  const {
    score,
    rangeType,
    ranges,
    widthReference,
    data,
    hightestValue,
    id,
    label,
    workouts,
    target,
    showTarget,
    scorePercent,
    showWorkoutPopup,
    sleepData,
    domain,
    healthySignals,
    overviewSummary
  } = props
  
  const cumulativePct = useMemo(() => {
    let total = data[widthReference]
    
    if (healthySignals && Array.isArray(healthySignals)) {
      const totalSleepModifiers = filterHealthySignalsByDate(healthySignals, label, sleepModifierMappings)
        .reduce((sum, item) => sum + Number(Number(item[`${sleepModifierMappings[label]}`]).toFixed(0)), 0)
      total += totalSleepModifiers
    }
    
    return total
  }, [data, healthySignals, label])
  
  const _width = getBarPCTWidth(cumulativePct, ranges)
  const width = hightestValue > target ? '99.5%' : Math.min(_width, 99.5) + '%'
  const [totalGradientWidth, setTotalGradientWidth] = useState(width)

  const svgWidth = 'calc(100% - 0.25rem)'
  return (
    <SegmentLineContainer>
      <div className="bar-container" style={{ width: '100%' }}>
        {hightestValue > score && rangeType === 'week' && (
          <div className="stripe-line">
            <StripeLine
              svgWidth={svgWidth}
              target={target}
              hightestValue={hightestValue}
              id={id}
            />
          </div>
        )}
        <div
          className="gradient-line"
          style={{ width: totalGradientWidth }}
        >
          <GradientLine
            svgWidth="calc(100vw - 1.75rem)"
            hightestValue={
              showTarget ? hightestValue : Math.max(scorePercent, 100)
            }
            id={`compact_${id}`}
            noRadius
          />
        </div>
        <div
          className="workout-line-score"
          style={{ width: '100%' }}
        >
          {workouts && rangeType === 'week' && (
            <Workouts
              totalPCT={score}
              workouts={workouts}
              label={label}
              ranges={ranges}
              totalWidth={width}
              containerStyle={{ top: 0 }}
              targetCursor={showTarget ? true : false}
              target={target}
              setTotalGradientWidth={setTotalGradientWidth}
              showWorkoutPopup={showWorkoutPopup}
              sleepData={sleepData}
              domain={domain}
              healthySignals={healthySignals}
              overviewSummary={overviewSummary}
            />
          )}
        </div>
      </div>
    </SegmentLineContainer>
  )
}
